import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GroupProvider } from './contexts/GroupContext';
import DemoSeedPage from './pages/DemoSeedPage';
import DemoCleanPage from './pages/DemoCleanPage';

import Home from './pages/Home';
import LoginPage from './pages/LoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminPage from './pages/AdminPage';
import ParticipantPage from './pages/ParticipantPage';
import SeedPage from './pages/SeedPage';
import SuperAdminPage from './pages/SuperAdminPage';
import CrearGrupoPage from './pages/CrearGrupoPage';
import MisGruposPage from './pages/MisGruposPage';
import TimezonePage from './pages/TimezonePage';
import PagoExitosoPage from './pages/PagoExitosoPage';
import './i18n';
import ConsentBanner from './components/ConsentBanner';
import Blog from './pages/blog'; 
import FixturePage from './pages/FixturePage';
import { useTranslation } from 'react-i18next';
import Privacidad from './pages/privacidad';
import Contacto from './pages/contacto';
import Terminos from './pages/terminos'; 
import { App as CapacitorApp } from '@capacitor/app';

// Firebase Messaging
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';

// Versión actual de la app
const APP_VERSION = "1.4.4";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.pitazomundial.app";

const SUPERADMIN_UID = 'qMaj1S0UFFQFmxJzYXZzYhEGcUr1';
const VAPID_KEY = 'BAqKjn0sjLBuhZSQCYEuIIYSz7xl1MNVC69tWWUq25XAcJKZTSrdXoO9GCJNxZZaZGoDhQfy-acDTTpk5tGBfko';

// AdMob IDs — TEST en debug, REAL en producción
const isNativeApp = () => window.Capacitor && window.Capacitor.isNative;
const ADMOB_APP_ID = 'ca-app-pub-4381314907397707~5772846281';
export const ADMOB_BANNER_ID = process.env.NODE_ENV === 'development'
  ? 'ca-app-pub-3940256099942544/6300978111'
  : 'ca-app-pub-4381314907397707/4978639361';
export const ADMOB_INTERSTITIAL_ID = process.env.NODE_ENV === 'development'
  ? 'ca-app-pub-3940256099942544/1033173712'
  : 'ca-app-pub-4381314907397707/7243943696';

async function initAdMob() {
  if (!isNativeApp()) return;
  try {
    const { AdMob } = await import('@capacitor-community/admob');
    await AdMob.initialize({
      requestTrackingAuthorization: false,
      testingDevices: [],
      initializeForTesting: process.env.NODE_ENV === 'development',
    });
    console.log('AdMob inicializado');
  } catch (err) {
    console.error('Error iniciando AdMob:', err);
  }
}

// ── ADSTERRA BANNER (solo web, solo grupos gratis) ───────
function AdsterraBanner({ activePlan }) {
  const { currentUser } = useAuth();
  const location = useLocation();
  const isNative = window.Capacitor?.isNative;

  // Solo en web, no en app nativa
  if (isNative) return null;
  // Solo si hay usuario logueado
  if (!currentUser) return null;
  // Solo en rutas de participante
  const path = location.pathname;
  const isNoAdRoute = ['/admin', '/superadmin', '/admin-login', '/seed', '/demo-seed', '/demo-clean', '/login', '/'].some(
    r => path === r
  ) || path.startsWith('/admin') || path.startsWith('/login');
  if (isNoAdRoute) return null;
  // Solo en grupos gratis
  if (!activePlan || activePlan !== 'free') return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 56, // encima del BottomNav
      left: '50%',
      transform: 'translateX(-50%)',
      width: 320,
      height: 50,
      zIndex: 100,
      overflow: 'hidden',
      borderRadius: 6,
      boxShadow: '0 -2px 10px rgba(0,0,0,0.3)',
    }}>
      <AdsterraAd />
    </div>
  );
}

// Componente que carga el banner de Adsterra una sola vez
function AdsterraAd() {
  const containerRef = React.useRef(null);
  React.useEffect(() => {
    if (!containerRef.current) return;
    if (containerRef.current.querySelector('iframe')) return;

    const conf = document.createElement('script');
    conf.innerHTML = `
      atOptions = {
        'key': 'd88d6addfe29f9f25f0201721c9eba9a',
        'format': 'iframe',
        'height': 50,
        'width': 320,
        'params': {}
      };
    `;
    const script = document.createElement('script');
    script.src = 'https://www.highperformanceformat.com/d88d6addfe29f9f25f0201721c9eba9a/invoke.js';
    script.async = true;

    containerRef.current.appendChild(conf);
    containerRef.current.appendChild(script);
  }, []);

  return <div ref={containerRef} style={{ width: 320, height: 50 }} />;
}

// Dispara el popunder de Adsterra (intersticial web) — solo grupos gratis, 1 vez por predicción
let adsterraPopunderLoaded = false;
export function showAdsterraPopunder(groupPlan) {
  // Solo en web, no en app nativa
  if (window.Capacitor?.isNative) return;
  // Solo en grupos gratis
  if (!groupPlan || groupPlan !== 'free') return;
  // Solo cargar el script una vez por sesión
  if (adsterraPopunderLoaded) return;
  adsterraPopunderLoaded = true;

  const script = document.createElement('script');
  script.src = 'https://pl29322608.profitablecpmratenetwork.com/89/2d/7d/892d7dc8dbf6946a1d26b87441cf6a3e.js';
  script.async = true;
  document.head.appendChild(script);
}

// ── ADMOB BANNER MANAGER (global, persiste entre páginas) ─────────────
function AdMobBannerManager({ splashDone }) {
  const { currentUser, userData } = useAuth();
  const location = useLocation();
  const [activePlan, setActivePlan] = React.useState(null);

  // Leer el plan del grupo activo cuando cambia el usuario o la ruta
  React.useEffect(() => {
    if (!currentUser) { setActivePlan(null); return; }
    const activeGroupId = localStorage.getItem('activeGroupId') || userData?.groupId;
    if (!activeGroupId) { setActivePlan('free'); return; }
    getDoc(doc(db, 'groups', activeGroupId))
      .then(snap => setActivePlan(snap.exists() ? (snap.data().plan || 'free') : 'free'))
      .catch(() => setActivePlan('free'));
  }, [currentUser, userData?.groupId, location.pathname]);

  // Mostrar u ocultar banner según plan y ruta — depende de activePlan
  useEffect(() => {
    if (!window.Capacitor?.isNative) return;
    if (!splashDone) return;
    if (!currentUser) {
      import('@capacitor-community/admob').then(({ AdMob }) => AdMob.removeBanner()).catch(() => {});
      return;
    }

    // Esperar a que se cargue el plan antes de decidir
    if (activePlan === null) return;

    // Sin anuncios si el grupo activo es de pago
    if (activePlan !== 'free') {
      import('@capacitor-community/admob').then(({ AdMob }) => AdMob.removeBanner()).catch(() => {});
      return;
    }

    const path = location.pathname;
    const isNoAdRoute = ['/admin', '/superadmin', '/admin-login', '/seed', '/demo-seed', '/demo-clean', '/login', '/'].some(
      r => path === r
    ) || path.startsWith('/admin') || path.startsWith('/login');

    if (isNoAdRoute) {
      import('@capacitor-community/admob').then(({ AdMob }) => AdMob.removeBanner()).catch(() => {});
      return;
    }

    const showBanner = async () => {
      try {
        const { AdMob, BannerAdSize, BannerAdPosition } = await import('@capacitor-community/admob');
        await AdMob.removeBanner().catch(() => {});
        await AdMob.showBanner({
          adId: ADMOB_BANNER_ID,
          adSize: BannerAdSize.BANNER,
          position: BannerAdPosition.BOTTOM_CENTER,
          margin: 0,
          isTesting: process.env.NODE_ENV === 'development',
        });
        console.log('AdMob banner activo en:', path);
      } catch (err) {
        console.error('AdMob banner error:', err);
      }
    };

    const timer = setTimeout(showBanner, 800);
    return () => clearTimeout(timer);
  }, [currentUser, userData, location.pathname, splashDone, activePlan]);

  return null;
}

async function registerFCMToken(userId) {
  try {
    let token = null;
    if (window.Capacitor && window.Capacitor.isNative) {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      await PushNotifications.requestPermissions();
      await PushNotifications.register();
      await new Promise((resolve) => {
        PushNotifications.addListener('registration', async (reg) => {
          token = reg.value;
          resolve();
        });
      });
    } else {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const messaging = getMessaging();
      token = await getToken(messaging, { vapidKey: VAPID_KEY });
      onMessage(messaging, (payload) => {
        console.log('Notificación en foreground:', payload);
      });
    }
    if (!token) return;
    const tokenId = `${userId}_${token.slice(-10)}`;
    await setDoc(doc(db, 'fcmTokens', tokenId), {
      userId,
      token,
      platform: window.Capacitor?.isNative ? 'android' : 'web',
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('Error registrando token FCM:', err);
  }
}

// --- SPLASH SCREEN ---
function SplashScreen({ onDone }) {
  const [fadeOut, setFadeOut] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const timersRef = useRef([]);
  
  const isNative = window.Capacitor && window.Capacitor.isNative;
  const totalTime = isNative ? 8500 : 4000;
  const barTime = totalTime - 600;

  const handleSkip = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    setFadeOut(true);
    setTimeout(() => onDone(), 600);
  }, [onDone]);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setFadeOut(true), barTime);
    const finishTimer = setTimeout(() => onDone(), totalTime);
    timersRef.current = [fadeTimer, finishTimer];

    if (isNative && videoRef.current) {
      videoRef.current.play().catch(e => console.log("Video wait:", e));
    }

    return () => {
      timersRef.current.forEach(t => clearTimeout(t));
    };
  }, [isNative, barTime, totalTime, onDone]);

  const handleUnmute = useCallback(() => {
    setMuted(false);
    if (videoRef.current) videoRef.current.muted = false;
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#080810',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      opacity: fadeOut ? 0 : 1,
      transition: 'opacity 0.6s ease',
      overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@700&display=swap');
        @keyframes splashFadeUp {
          0%   { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes splashBarFill {
          0%   { width: 0%; }
          100% { width: 100%; }
        }
        @keyframes kenBurnsEffect {
          0%   { transform: scale(1); }
          100% { transform: scale(1.1); }
        }
      `}</style>

      {isNative ? (
        <video
          ref={videoRef}
          autoPlay loop muted={muted} playsInline
          onCanPlay={() => setVideoReady(true)}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', opacity: videoReady ? 1 : 0, transition: 'opacity 1.2s ease',
          }}
        >
          <source src="/stadium.mp4" type="video/mp4" />
        </video>
      ) : (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url("/stadium-bg.jpg")',
          backgroundSize: 'cover', backgroundPosition: 'center',
          animation: `kenBurnsEffect ${totalTime}ms ease-out forwards`,
        }} />
      )}

      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, rgba(8,8,16,0.2) 0%, rgba(8,8,16,0.5) 50%, rgba(8,8,16,0.85) 100%)',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <div style={{
          fontFamily: "'Oswald', sans-serif",
          fontSize: '3rem', fontWeight: 700, letterSpacing: '6px',
          animation: 'splashFadeUp 0.7s ease both',
          marginBottom: '0.5rem',
          textShadow: '0 4px 15px rgba(0,0,0,0.7)',
          color: '#F5F5F0'
        }}>
          PITAZO <span style={{ color: '#F0B429' }}>MUNDIAL</span>
        </div>

        <div style={{
          fontSize: '0.75rem', letterSpacing: '4px', color: '#ccc',
          animation: 'splashFadeUp 0.7s ease 0.3s both',
          marginBottom: '3.5rem',
          textShadow: '0 2px 8px rgba(0,0,0,0.8)',
        }}>LA POLLÁ DEL MUNDIAL 2026</div>

        <div style={{
          width: '220px', height: '3px',
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '2px', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #F0B429, #ffdd70)',
            borderRadius: '2px',
            animation: `splashBarFill ${barTime}ms linear both`,
            boxShadow: '0 0 10px rgba(240,180,41,0.7)'
          }} />
        </div>

        {isNative && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 28 }}>
            <button
              onClick={handleUnmute}
              style={{
                background: muted ? 'rgba(255,255,255,0.08)' : 'rgba(240,180,41,0.15)',
                border: `1px solid ${muted ? 'rgba(255,255,255,0.15)' : 'rgba(240,180,41,0.4)'}`,
                borderRadius: 20, padding: '8px 16px',
                color: muted ? '#6B6B7B' : '#F0B429',
                fontSize: 13, cursor: 'pointer',
                fontFamily: "'Oswald', sans-serif", letterSpacing: 1,
                transition: 'all 0.2s',
              }}
            >
              {muted ? '🔇 SONIDO' : '🔊 SONIDO'}
            </button>
            <button
              onClick={handleSkip}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 20, padding: '8px 20px',
                color: '#aaa', fontSize: 13, cursor: 'pointer',
                fontFamily: "'Oswald', sans-serif", letterSpacing: 1,
                transition: 'all 0.2s',
              }}
            >
              SALTAR →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// --- GLOBAL FOOTER PARA ADSENSE (ACTUALIZADO CON BLOG) ---
// --- GLOBAL FOOTER TRADUCIDO ---
function GlobalFooter() {
  const { t } = useTranslation();
  const location = useLocation();
  const hideFooter = ['/admin', '/superadmin', '/admin-login', '/login', '/seed', '/demo-seed', '/demo-clean'].some(path => location.pathname === path || location.pathname.startsWith('/admin'));
  if (hideFooter) return null;
  // ✅ Define el estilo aquí
  const footerLinkStyle = { color: '#888', textDecoration: 'none', margin: '0 12px', fontSize: '12px' };
  
  return (
    <footer style={{
      background: '#0d0d1a',
      borderTop: '1px solid rgba(255,255,255,0.07)',
      padding: '32px 20px',
      textAlign: 'center',
      marginTop: 'auto'
    }}>
      <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: 3, marginBottom: 16 }}>
        PITAZO <span style={{ color: '#F0B429' }}>MUNDIAL</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
        <a href="/blog" style={footerLinkStyle}>{t('blog_link')}</a>
        <a href="/fixture" style={footerLinkStyle}>{t('fixture_link')}</a>
        <a href="/privacy" style={footerLinkStyle}>{t('privacy')}</a>
        <a href="/contact" style={footerLinkStyle}>{t('contact_link')}</a>
        <a href="/terms" style={footerLinkStyle}>{t('terms_link')}</a>
      </div>
      <div style={{ fontSize: 11, color: '#333' }}>© 2026 Pitazo Mundial · {t('footer_rights')}</div>
    </footer>
  );
}

// --- RUTAS Y AUTH ---
function AdminRoute({ children }) {
  const { currentUser, userData, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!currentUser) return <Navigate to="/admin-login" />;
  if (userData?.role !== 'admin') return <Navigate to="/dashboard" />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { currentUser, loading } = useAuth();
  
  console.log('🔍 SuperAdminRoute Debug:', {
    loading,
    currentUserExists: !!currentUser,
    currentUserUid: currentUser?.uid,
    superAdminUid: SUPERADMIN_UID,
    uidMatch: currentUser?.uid === SUPERADMIN_UID,
    timestamp: new Date().toISOString()
  });
  
  if (loading) {
    console.log('⏳ SuperAdmin: Esperando carga de auth...');
    return <LoadingScreen />;
  }
  
  if (!currentUser) {
    console.log('❌ SuperAdmin: No hay usuario logueado, redirigiendo a /admin-login');
    return <Navigate to="/admin-login" />;
  }
  
  if (currentUser.uid !== SUPERADMIN_UID) {
    console.log('❌ SuperAdmin: UID no coincide', {
      recibido: currentUser.uid,
      esperado: SUPERADMIN_UID
    });
    return <Navigate to="/admin" />;
  }
  
  console.log('✅ SuperAdmin: Acceso concedido a UID:', currentUser.uid);
  return children;
}

function ParticipantRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!currentUser) return <Navigate to="/" />;
  return children;
}

function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', background: '#080810', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '4px solid rgba(255,255,255,0.1)', borderTop: '4px solid #F0B429', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, letterSpacing: 2, color: '#F0B429' }}>PITAZO<span style={{ color: '#F5F5F0' }}>MUNDIAL</span></div>
      </div>
    </div>
  );
}

function FCMRegister() {
  const { currentUser, userData } = useAuth();
  useEffect(() => {
    if (currentUser) registerFCMToken(currentUser.uid);
  }, [currentUser]);
  return null;
}

// ✅ APP ROUTES MODIFICADA CON GLOBAL FOOTER Y RUTA BLOG
// ── MODAL DE ACTUALIZACIÓN OBLIGATORIA ────────────────────────
function UpdateModal() {
  const [needsUpdate, setNeedsUpdate] = React.useState(false);
  const [changelog, setChangelog] = useState("");

  React.useEffect(() => {
    // Esperar 2 segundos para que Capacitor esté completamente inicializado
    const timer = setTimeout(() => {
      const isNative = !!(window.Capacitor && (window.Capacitor.isNative || window.Capacitor.isNativePlatform?.()));
    
      // Solo en app nativa — en web nunca mostrar
      if (!isNative) return;

      getDoc(doc(db, "config", "appVersion"))
        .then(snap => {
          if (!snap.exists()) return;
          const minVersion = snap.data().minVersion || "1.0.0";
          const changelog = snap.data().changelog || "";
          const parseV = v => v.split(".").map(Number);
          const [ma, mi, pa] = parseV(APP_VERSION);
          const [mb, mib, pb] = parseV(minVersion);
          const outdated = ma < mb || (ma === mb && mi < mib) || (ma === mb && mi === mib && pa < pb);
          if (outdated) {
            setChangelog(changelog);
            setNeedsUpdate(true);
          }
        })
        .catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  if (!needsUpdate) return null;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(8,8,16,0.97)", zIndex: 99999,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "2rem", fontFamily: "Oswald, Arial Black, sans-serif"
    }}>
      <div style={{
        background: "#14141f", border: "1px solid rgba(240,180,41,0.3)",
        borderRadius: 16, padding: "2.5rem 2rem", maxWidth: 360,
        width: "100%", textAlign: "center", color: "#F5F5F0"
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🚀</div>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 2, marginBottom: 8 }}>
          NUEVA VERSIÓN <span style={{ color: "#F0B429" }}>DISPONIBLE</span>
        </div>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#9B9BAA", marginBottom: 24, lineHeight: 1.6 }}>
          Hay una actualización necesaria para continuar usando Pitazo Mundial. Por favor actualiza la app desde el Play Store.
        </div>

        {changelog && (
          <div style={{
            background: "rgba(240,180,41,0.06)",
            border: "1px solid rgba(240,180,41,0.2)",
            borderRadius: 10, padding: "12px 14px",
            marginBottom: 24, textAlign: "left"
          }}>
            <div style={{ fontSize: 11, color: "#F0B429", letterSpacing: 1, marginBottom: 6 }}>
              ✨ NOVEDADES
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#ccc", lineHeight: 1.5 }}>
              {changelog}
            </div>
          </div>
        )}

        <button
          onClick={() => window.open(PLAY_STORE_URL, "_blank")}
          style={{
            width: "100%", padding: "14px 0",
            background: "#F0B429", border: "none", borderRadius: 10,
            fontFamily: "Oswald, sans-serif", fontSize: 16, fontWeight: 700,
            color: "#080810", cursor: "pointer", letterSpacing: 1
          }}
        >
          📲 ACTUALIZAR AHORA
        </button>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { currentUser, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/demo-seed" element={<DemoSeedPage />} />
        <Route path="/demo-clean" element={<DemoCleanPage />} />
        <Route path="/login/:linkId" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin-login" element={<AdminLoginPage />} />
        <Route path="/seed" element={<SeedPage />} />
        <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminPage /></SuperAdminRoute>} />
        <Route path="/admin" element={<AdminRoute><GroupProvider><AdminPage /></GroupProvider></AdminRoute>} />
        <Route path="/crear-grupo" element={<ParticipantRoute><CrearGrupoPage /></ParticipantRoute>} />
        <Route path="/dashboard/:groupId?" element={<ParticipantRoute><GroupProvider><ParticipantPage /></GroupProvider></ParticipantRoute>} />
        <Route path="/mis-grupos" element={<ParticipantRoute><MisGruposPage /></ParticipantRoute>} />
        <Route path="/timezone-test" element={<TimezonePage />} />
        <Route path="/pago-exitoso" element={<PagoExitosoPage />} />
        <Route path="/pago-fallido" element={<Navigate to="/admin" />} />
        <Route path="/pago-pendiente" element={<Navigate to="/admin" />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="*" element={currentUser ? <Navigate to="/mis-grupos" /> : <Navigate to="/" />} />
        <Route path="/fixture" element={<FixturePage />} />
        <Route path="/privacy" element={<Privacidad />} />
        <Route path="/contact" element={<Contacto />} />
        <Route path="/terms" element={<Terminos />} />
        
      </Routes>
      <GlobalFooter />
    </div>
  );
}

// ── ADSTERRA WRAPPER (lee el plan y renderiza el banner) ─
function AdsterraWrapper() {
  const { currentUser, userData } = useAuth();
  const location = useLocation();
  const [activePlan, setActivePlan] = React.useState(null);

  React.useEffect(() => {
    if (!currentUser) { setActivePlan(null); return; }
    const activeGroupId = localStorage.getItem('activeGroupId') || userData?.groupId;
    if (!activeGroupId) { setActivePlan('free'); return; }
    getDoc(doc(db, 'groups', activeGroupId))
      .then(snap => setActivePlan(snap.exists() ? (snap.data().plan || 'free') : 'free'))
      .catch(() => setActivePlan('free'));
  }, [currentUser, userData?.groupId, location.pathname]);

  return <AdsterraBanner activePlan={activePlan} />;
}

export default function App() {
  const [splashDone, setSplashDone] = useState(() => {
    return sessionStorage.getItem('splashShown') === 'true';
  });

  // Inicializar AdMob al arrancar (no esperar al splash)
  useEffect(() => {
    initAdMob();
  }, []);

  const handleSplashDone = useCallback(() => {
    setSplashDone(true);
    sessionStorage.setItem('splashShown', 'true');
  }, []);

  // 👇 useEffect para restaurar sesión al volver a la app (¡AHORA ESTÁ BIEN UBICADO!)
  useEffect(() => {
    // Solo para app nativa (Capacitor)
    if (window.Capacitor?.isNative) {
      const handleAppStateChange = ({ isActive }) => {
        if (isActive) {
          // Esperar 5 segundos — evita interrumpir Google Sign-In
          setTimeout(() => {
            const isLoggingIn = sessionStorage.getItem('googleSignInInProgress') === 'true';
            if (!auth.currentUser && !isLoggingIn) {
              console.log('🔄 Sesión no detectada al regresar. Recargando para restaurar...');
              window.location.reload();
            }
          }, 5000);
        }
      };

      CapacitorApp.addListener('appStateChange', handleAppStateChange);

      // Limpiar el listener al desmontar el componente
      return () => {
        CapacitorApp.removeListener('appStateChange', handleAppStateChange);
      };
    }
  }, []); // El array vacío asegura que se ejecute solo una vez

  return (
    <BrowserRouter>
      <AuthProvider>
        {!splashDone && <SplashScreen onDone={handleSplashDone} />}
        <UpdateModal />
        <FCMRegister />
        <AdMobBannerManager splashDone={splashDone} />
        <AdsterraWrapper />
        <ConsentBanner />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}